import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, DollarSign, Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsAPI } from '../lib/api';

const BottlePricingSettings = forwardRef((props, ref) => {
  const queryClient = useQueryClient();
  const [prices, setPrices] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['bottle-prices'],
    queryFn: async () => {
      const response = await settingsAPI.getBottlePrices();
      return response.data;
    }
  });

  useEffect(() => {
    if (data?.prices) {
      setPrices(JSON.parse(JSON.stringify(data.prices)));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (nextPrices) => settingsAPI.updateBottlePrices(nextPrices),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bottle-prices'] });
      setPrices(response.data.prices);
      toast.success('Bottle prices saved. Existing products are unchanged until you apply.');
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to save bottle prices');
    }
  });

  const applyMutation = useMutation({
    mutationFn: () => settingsAPI.applyBottlePrices({}),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['bottle-prices'] });
      toast.success(response.data.message || `Updated ${response.data.updated} products`);
    },
    onError: (err) => {
      toast.error(err.response?.data?.error || 'Failed to apply prices to existing products');
    }
  });

  const handlePriceChange = (categoryKey, size, value) => {
    setPrices((prev) => ({
      ...prev,
      [categoryKey]: {
        ...prev[categoryKey],
        [String(size)]: value
      }
    }));
  };

  const savePrices = async () => {
    if (!prices) {
      toast.error('Bottle prices are still loading');
      return false;
    }
    const numericPrices = {};
    for (const [categoryKey, sizes] of Object.entries(prices)) {
      numericPrices[categoryKey] = {};
      for (const [size, value] of Object.entries(sizes || {})) {
        numericPrices[categoryKey][size] = Number(String(value).replace(/,/g, ''));
      }
    }
    await saveMutation.mutateAsync(numericPrices);
    return true;
  };

  useImperativeHandle(ref, () => ({
    save: savePrices
  }), [prices]);

  const handleSave = () => {
    savePrices().catch(() => {});
  };

  const handleApply = () => {
    const confirmed = window.confirm(
      'Apply the saved bottle prices to existing perfume products? This updates products already in shops.'
    );
    if (!confirmed) return;
    applyMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="p-8 text-center text-gray-500">
        Loading bottle prices...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-red-600">
        Failed to load bottle prices. Please try again.
      </div>
    );
  }

  const categories = data?.categories || [];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Bottle prices</h2>
        <p className="text-sm text-gray-500 mt-1">
          Set the selling price in RWF used when bottling. Saving does not change products already in shops unless you apply.
        </p>
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
        <p>
          New bottling uses these prices immediately. Click <strong>Apply to existing products</strong> only when you want current shop stock to match.
        </p>
      </div>

      <div className="space-y-5">
        {categories.map((category) => (
          <div key={category.key} className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">{category.label}</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {category.sizes.map((size) => (
                <label key={size} className="flex items-center justify-between gap-4 px-4 py-3">
                  <span className="text-sm text-gray-700">{size}ml</span>
                  <div className="relative w-40">
                    <span className="absolute inset-y-0 left-3 flex items-center text-xs text-gray-400">
                      RWF
                    </span>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={prices?.[category.key]?.[String(size)] ?? ''}
                      onChange={(e) => handlePriceChange(category.key, size, e.target.value)}
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 rounded-lg text-right text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={handleApply}
          disabled={applyMutation.isPending || saveMutation.isPending}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <DollarSign className="h-4 w-4 mr-2" />
          {applyMutation.isPending ? 'Applying...' : 'Apply to existing products'}
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saveMutation.isPending || !prices}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending ? 'Saving...' : 'Save prices'}
        </button>
      </div>
    </div>
  );
});

BottlePricingSettings.displayName = 'BottlePricingSettings';

export default BottlePricingSettings;
